import { __assign } from "tslib";
import isPromise from 'is-promise';
import { getDataState } from './state-helper';
var DataLoaderStoreAndLoader = (function () {
    function DataLoaderStoreAndLoader(onEvent, initialState, performLoad, createParamsHash, isServerSideRender) {
        this.performLoad = performLoad;
        this.createParamsHash = createParamsHash;
        this.isServerSideRender = isServerSideRender;
        this.requiresUpdateOnMount = [];
        this.registeredDataLoaders = {};
        this.paramHashConsumers = {};
        this.paramHashVersion = {};
        this.dataStore = {};
        this.stateVersionCounter = 0;
        this.currentWorkCount = 0;
        this.onEvent = function (event) {
            try {
                return onEvent(event);
            }
            catch (err) {
                console.error('onEvent handler threw', err);
            }
        };
        if (initialState) {
            this.dataStore = initialState;
        }
        else {
            this.onEvent({
                type: 'state-changed',
                state: __assign({}, this.dataStore),
            });
        }
    }
    DataLoaderStoreAndLoader.prototype.attach = function (componentInstanceId, resourceType, dataLoadParams, update) {
        var paramsObjectHash = this.createParamsHash(resourceType, dataLoadParams);
        this.registerDataLoaderHash(paramsObjectHash, componentInstanceId);
        this.registeredDataLoaders[componentInstanceId] = {
            update: update,
            currentParamsHash: paramsObjectHash,
        };
        var requiresUpdateIndex = this.requiresUpdateOnMount.indexOf(componentInstanceId);
        if (requiresUpdateIndex !== -1) {
            this.requiresUpdateOnMount.splice(requiresUpdateIndex, 1);
            if (this.dataStore[paramsObjectHash] &&
                this.dataStore[paramsObjectHash].status !== "Fetching") {
                update();
            }
        }
    };
    DataLoaderStoreAndLoader.prototype.detach = function (componentInstanceId, resourceType, dataLoadParams) {
        var paramsObjectHash = this.createParamsHash(resourceType, dataLoadParams);
        this.cleanupDataLoader(paramsObjectHash, componentInstanceId);
        delete this.registeredDataLoaders[componentInstanceId];
    };
    DataLoaderStoreAndLoader.prototype.getDataLoaderState = function (componentInstanceId, resourceType, dataLoadParams, keepData, forceRefresh) {
        if (keepData === void 0) { keepData = false; }
        if (forceRefresh === void 0) { forceRefresh = false; }
        var previousRenderParamsObjectHash = this.registeredDataLoaders[componentInstanceId]
            ? this.registeredDataLoaders[componentInstanceId].currentParamsHash
            : undefined;
        var paramsObjectHash = this.createParamsHash(resourceType, dataLoadParams);
        var stateForParams = this.dataStore[paramsObjectHash];
        if (previousRenderParamsObjectHash &&
            paramsObjectHash !== previousRenderParamsObjectHash &&
            !keepData) {
            this.cleanupDataLoader(previousRenderParamsObjectHash, componentInstanceId);
        }
        if (stateForParams && !forceRefresh) {
            return stateForParams;
        }
        var wasRegistered = this.registerDataLoaderHash(paramsObjectHash, componentInstanceId);
        var result;
        try {
            result = this.performLoad(__assign(__assign({}, dataLoadParams), { resourceType: resourceType, paramsCacheKey: paramsObjectHash }));
        }
        catch (err) {
            this.updateParamsHashState(paramsObjectHash, {
                status: "Idle",
                lastAction: {
                    type: 'fetch',
                    success: false,
                    error: err,
                },
                data: getDataState(true, previousRenderParamsObjectHash, this.dataStore),
            });
            return this.dataStore[paramsObjectHash];
        }
        if (isPromise(result)) {
            if (wasRegistered) {
                this.requiresUpdateOnMount.push(componentInstanceId);
            }
            this.updateParamsHashState(paramsObjectHash, {
                status: "Fetching",
                lastAction: {
                    type: 'none',
                    success: true,
                },
                data: getDataState(keepData, previousRenderParamsObjectHash, this.dataStore),
            });
            this.onEvent({
                type: 'begin-loading-event',
                data: {
                    resourceLoadParamsHash: paramsObjectHash,
                    resourceType: resourceType,
                },
            });
            this.monitorLoad(resourceType, paramsObjectHash, result, this.stateVersionCounter);
        }
        else {
            this.updateParamsHashState(paramsObjectHash, {
                status: "Idle",
                lastAction: {
                    type: 'fetch',
                    success: true,
                },
                data: {
                    hasData: true,
                    dataFromServerSideRender: this.isServerSideRender,
                    result: result,
                },
            });
        }
        return this.dataStore[paramsObjectHash];
    };
    DataLoaderStoreAndLoader.prototype.monitorLoad = function (resourceType, paramsObjectHash, work, beginLoadVersion) {
        var _this = this;
        this.currentWorkCount++;
        work.then(function (result) { return ({ success: true, result: result }); }, function (err) { return ({ success: false, err: err }); }).then(function (result) {
            var currentState = _this.dataStore[paramsObjectHash];
            var currentStateVersion = _this.paramHashVersion[paramsObjectHash];
            var versionMismatch = currentState && currentStateVersion !== beginLoadVersion;
            var dataLoadersForHash = _this.paramHashConsumers[paramsObjectHash];
            if (!versionMismatch && dataLoadersForHash) {
                if (result.success) {
                    _this.updateParamsHashState(paramsObjectHash, {
                        status: "Idle",
                        lastAction: {
                            type: 'fetch',
                            success: true,
                        },
                        data: {
                            hasData: true,
                            dataFromServerSideRender: _this.isServerSideRender,
                            result: result.result,
                        },
                    });
                }
                else {
                    _this.updateParamsHashState(paramsObjectHash, {
                        status: "Idle",
                        lastAction: {
                            type: 'fetch',
                            success: false,
                            error: result.err,
                        },
                        data: currentState.data,
                    });
                }
                dataLoadersForHash.forEach(function (dataLoader) {
                    var dataLoaderInfo = _this.registeredDataLoaders[dataLoader];
                    if (dataLoaderInfo) {
                        dataLoaderInfo.update();
                    }
                });
            }
            if (!result.success) {
                _this.onEvent({
                    type: 'load-error',
                    data: {
                        resourceType: resourceType,
                        resourceLoadParamsHash: paramsObjectHash,
                        error: result.err,
                    },
                });
            }
            _this.onEvent({
                type: 'end-loading-event',
                data: {
                    resourceType: resourceType,
                    resourceLoadParamsHash: paramsObjectHash,
                },
            });
            var currentCount = --_this.currentWorkCount;
            if (currentCount === 0) {
                _this.onEvent({
                    type: 'data-load-completed',
                    data: {
                        resourceType: resourceType,
                        resourceLoadParamsHash: paramsObjectHash,
                    },
                });
            }
        });
    };
    DataLoaderStoreAndLoader.prototype.registerDataLoaderHash = function (paramsObjectHash, componentInstanceId) {
        if (!this.paramHashConsumers[paramsObjectHash]) {
            this.paramHashConsumers[paramsObjectHash] = [];
        }
        if (this.paramHashConsumers[paramsObjectHash].indexOf(componentInstanceId) === -1) {
            this.paramHashConsumers[paramsObjectHash].push(componentInstanceId);
            return true;
        }
        return false;
    };
    DataLoaderStoreAndLoader.prototype.cleanupDataLoader = function (previousRenderParamsObjectHash, componentInstanceId) {
        var consumersForPreviousHash = this.paramHashConsumers[previousRenderParamsObjectHash];
        if (!consumersForPreviousHash) {
            return;
        }
        consumersForPreviousHash.splice(consumersForPreviousHash.indexOf(componentInstanceId), 1);
        if (consumersForPreviousHash.length === 0) {
            delete this.dataStore[previousRenderParamsObjectHash];
            delete this.paramHashConsumers[previousRenderParamsObjectHash];
            this.onEvent({
                type: 'state-changed',
                state: __assign({}, this.dataStore),
            });
        }
    };
    DataLoaderStoreAndLoader.prototype.updateParamsHashState = function (paramsObjectHash, newState) {
        this.dataStore[paramsObjectHash] = newState;
        this.paramHashVersion[paramsObjectHash] =
            (this.paramHashVersion[paramsObjectHash] || -1) + 1;
        this.onEvent({
            type: 'state-changed',
            state: __assign({}, this.dataStore),
        });
    };
    return DataLoaderStoreAndLoader;
}());
export { DataLoaderStoreAndLoader };
//# sourceMappingURL=data-loader-store-and-loader.js.map